import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Heart, PawPrint, ChartLineUp, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import BigDonorWall from "@/components/BigDonorWall";

const PRESETS = [25, 50, 100, 250, 500];

export default function Donate() {
  const [amount, setAmount] = useState(100);
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [tracker, setTracker] = useState({ raised: 0, goal: 3000000, count: 0 });
  const [thankYou, setThankYou] = useState(false);

  const loadTracker = () => api.get("/donations/total").then(({ data }) => setTracker(data)).catch(() => {});

  useEffect(() => {
    loadTracker();
    const params = new URLSearchParams(window.location.search);
    if (params.get("donation") === "success" && params.get("session_id")) {
      const sid = params.get("session_id");
      let tries = 0;
      const poll = setInterval(async () => {
        tries += 1;
        try {
          const { data } = await api.get(`/donations/status/${sid}`);
          if (data.payment_status === "paid") { clearInterval(poll); setThankYou(true); loadTracker(); }
        } catch (e) { console.debug("donation status poll retry", e); }
        if (tries > 6) clearInterval(poll);
      }, 1500);
    } else if (params.get("donation") === "cancel") {
      toast("Donation canceled — the gates are still open when you're ready.");
    }
  }, []);

  const value = custom ? Math.max(0, parseFloat(custom) || 0) : amount;
  const pct = Math.min(100, Math.round((tracker.raised / tracker.goal) * 100));

  const donate = async () => {
    if (value < 1) { toast.error("Minimum donation is $1"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/donations/checkout", { amount: value, origin_url: window.location.origin, name, email });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Couldn't start checkout — try again");
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16" data-testid="donate-page">
      <p className="overline text-primary mb-3 inline-flex items-center gap-2"><Heart weight="fill" size={14} /> Build Heaven</p>
      <h1 className="font-display text-4xl sm:text-5xl leading-[1.02] mb-4">Open the gates. <span className="neon-text">Bring 'em home.</span></h1>
      <p className="text-muted-foreground max-w-2xl leading-relaxed mb-10">
        Every dollar funds the fences, the food and the freedom — 300 acres, 3,000 pits, a $3M sanctuary called Heaven. For all the babies who just wanna go home.
      </p>

      {/* Live tracker */}
      <div className="rounded-2xl border border-primary/30 bg-card p-7 sm:p-9 neon-glow mb-10" data-testid="donate-tracker">
        <div className="flex items-end justify-between mb-3">
          <span className="text-2xl sm:text-3xl font-modern" data-testid="donate-raised">${Number(tracker.raised).toLocaleString()}</span>
          <span className="overline text-muted-foreground">of ${Number(tracker.goal).toLocaleString()} · {pct}%</span>
        </div>
        <div className="h-4 w-full rounded-full bg-white/10 overflow-hidden">
          <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 1.2, ease: "easeOut" }}
            className="h-full rounded-full bg-gradient-to-r from-primary to-secondary" data-testid="donate-bar" />
        </div>
        <p className="overline text-secondary mt-3 inline-flex items-center gap-2"><ChartLineUp weight="bold" size={13} /> Live · {tracker.count} gifts counted toward Heaven</p>
      </div>

      {thankYou ? (
        <div className="rounded-2xl border border-primary/40 bg-card p-9 text-center neon-glow" data-testid="donate-thankyou">
          <Check weight="bold" size={40} className="text-primary mx-auto mb-4" />
          <h2 className="font-display text-3xl mb-2">Thank you for joining the fam.</h2>
          <p className="text-muted-foreground">Your gift just brought Heaven one step closer. The whole pack thanks you. 🐾</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <p className="overline text-muted-foreground mb-3">Choose an amount</p>
            <div className="flex flex-wrap gap-3 mb-4">
              {PRESETS.map((p) => (
                <button key={p} onClick={() => { setAmount(p); setCustom(""); }}
                  className={`px-5 py-3 rounded-[10px] font-modern border transition-colors ${!custom && amount === p ? "bg-primary text-primary-foreground border-primary" : "border-white/20 text-white hover:border-primary/50"}`}
                  data-testid={`donate-preset-${p}`}>${p}</button>
              ))}
            </div>
            <input type="number" min="1" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Custom amount ($)"
              className="w-full bg-white/5 border border-white/15 rounded-[10px] px-4 py-3 text-white outline-none focus:border-primary/50" data-testid="donate-custom" />
          </div>
          <div>
            <p className="overline text-muted-foreground mb-3">Your details (optional)</p>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name"
              className="w-full bg-white/5 border border-white/15 rounded-[10px] px-4 py-3 text-white outline-none focus:border-primary/50 mb-3" data-testid="donate-name" />
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (for your receipt)"
              className="w-full bg-white/5 border border-white/15 rounded-[10px] px-4 py-3 text-white outline-none focus:border-primary/50 mb-5" data-testid="donate-email" />
            <button onClick={donate} disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground py-4 rounded-[10px] font-modern font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50" data-testid="donate-submit">
              <PawPrint weight="fill" size={18} /> {busy ? "Opening checkout…" : `Donate $${value || 0}`}
            </button>
            <p className="text-xs text-muted-foreground mt-3 text-center">Secure checkout via Stripe. Test card: 4242 4242 4242 4242</p>
          </div>
        </div>
      )}

      {/* Big-Donor Wall + Lives Saved counter */}
      <BigDonorWall />
    </div>
  );
}
